/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from 'providers/AccountProvider';
import { useNavigate } from 'react-router-dom';
import { removeCookie, SESSION_COOKIE_KEY } from 'services/cookies';
import { Spin, notification } from 'antd';
import { motion } from 'framer-motion';
import TravelAgencyIcon from 'assets/icons/accommodation_icon.svg';
import './styles.scss';
import { axios } from 'startup';
import config from 'config';
import { ITravelAgencyDTO } from 'DTOModels/travelAgencyDTO';
import { travelAgencyMotion, travelAgencyListItemMotion } from './animation';
import TravelAgencyListItem from './components/TravelAgencyListItem';

const top = 10;

function TravelAgencies() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [hasMore, setHasMore] = useState(true);
    const { setAccount } = useAccount();
    const [items, setItems] = useState<ITravelAgencyDTO[]>([]);

    const logout = useCallback(() => {
        // timeout just to add a bit of delay
        setTimeout(() => {
            removeCookie(SESSION_COOKIE_KEY);
            if (setAccount) setAccount(undefined);
            navigate('/login');
        }, 250);
        notification.error({
            message: t('general.ERROR'),
            description: t('general.SUSPICIOUS_ACTIVITY'),
        });
    }, []);

    const fetchList = useCallback(() => {
        const currentDate = new Date();
        setHasMore(true);
        setLoading(true);
        axios
            .get(`${config.API_URL}/travel?top=${top}&date=${currentDate}`)
            .then(response => {
                setItems(
                    response.data.TravelAgencies.map(
                        (item: ITravelAgencyDTO) => ({
                            ...item,
                        }),
                    ),
                );
                if (response.data.Total <= top) setHasMore(false);
                setLoading(false);
            })
            .catch(() => {
                // force logout
                logout();
            });
    }, []);

    const fetchMore = () => {
        const lastId = items[items.length - 1].Id;
        const currentDate = new Date();
        axios
            .get(
                `${config.API_URL}/travel?top=${top}&lastId=${lastId}&date=${currentDate}`,
            )
            .then(response => {
                setItems(m => [
                    ...m,
                    ...response.data.TravelAgencies.filter(
                        (a: ITravelAgencyDTO) =>
                            !items.some(e => e.Id === a.Id),
                    ),
                ]);
                if (response.data.Total <= top) setHasMore(false);
            })
            .catch(() => {
                // force logout
                logout();
            });
    };

    useEffect(() => {
        fetchList();
    }, []);

    return (
        <div className='travel-agencies'>
            <div className='travel-agencies__content'>
                {loading && (
                    <div className='travel-agencies__content__empty'>
                        <Spin className='' size='large' />
                    </div>
                )}
                {!loading && items.length === 0 && (
                    <motion.div
                        {...travelAgencyMotion}
                        className='travel-agencies__content__empty'
                    >
                        <img src={TravelAgencyIcon} alt='Travel Agency Icon' />
                        <p>
                            {t(
                                'accommodationAndGastronomy.NO_TRAVEL_AGENCY_REGISTERED',
                            )}
                        </p>
                    </motion.div>
                )}
                {!loading && items.length > 0 && (
                    <motion.div
                        {...travelAgencyMotion}
                        className='travel-agencies__content__list'
                    >
                        {items.map(
                            (
                                travelAgencyItem: ITravelAgencyDTO,
                                index: number,
                            ) => {
                                return (
                                    <motion.div
                                        {...travelAgencyListItemMotion}
                                        className='travel-agencies__content__list__item'
                                        key={index}
                                    >
                                        <TravelAgencyListItem
                                            travelAgency={travelAgencyItem}
                                        />
                                    </motion.div>
                                );
                            },
                        )}
                    </motion.div>
                )}
            </div>
            {!loading && items.length > 0 && hasMore && (
                <button
                    type='button'
                    className='travel-agencies__button'
                    onClick={() => {
                        fetchMore();
                    }}
                >
                    {t('general.SEE_MORE')}
                </button>
            )}
        </div>
    );
}

export default TravelAgencies;
